import React, { useState, useEffect } from 'react';
import { cn } from './utility';
import MenuIcon from './MenuIcon';
import CreateLink from './CreateLink';

const textStyles = [
    'text-gray-800',
    'hover:text-black',

    // dark mode
    'dark:text-blue-gray-150',
    'dark:hover:text-blue-gray-150',
];

const activeStyles = [
    'alli-sidebar-active',
    'bg-blue-gray-100',

    // dark mode
    'dark:bg-blue-gray-900',
].join(' ');

const hoverStyles = [
    'hover:bg-blue-gray-100',
    'dark:hover:bg-blue-gray-900',
    'cursor-pointer',
].join(' ');

const defaultStyles = [
    ...textStyles,

    'group',
    'w-full',
    'rounded-md',
    'justify-start',
    'items-center',
    'inline-flex',
    'text-gray-800',
    'mb-1',
    'transition',
    'duration-150',
];

export default function MenuItemNew({
    item,
    isActive,
    activeSubnavItem,
    navigateToUrl,
    isSidebarOpen,
}) {
    const [isSubnavOpen, setSubnavOpen] = useState(
        isSidebarOpen && isActive && item.subNav?.length > 0,
    );

    useEffect(() => {
        setSubnavOpen(isSidebarOpen && isActive && item.subNav?.length > 0);
    }, [isSidebarOpen, isActive, item?.subNav?.length]);

    if (item.hideInAlli360) {
        return null;
    }

    return (
        <>
            <div
                className={cn(defaultStyles, {
                    [hoverStyles]: isSidebarOpen,
                    // only show our active styles at the top level if a subnav
                    // item is not active.
                    // Also show the top level item as active if the sidebar
                    // is closed.
                    [activeStyles]:
                        (isActive && !activeSubnavItem) ||
                        (isActive && !isSidebarOpen),
                })}
                id={item.id}
            >
                {item?.subNav?.length > 0 ? (
                    <>
                        <div
                            className="flex items-center h-auto w-full px-3 py-2 gap-2.5"
                            onClick={
                                isSidebarOpen
                                    ? () => setSubnavOpen(!isSubnavOpen)
                                    : undefined
                            }
                            aria-hidden="true"
                        >
                            {item?.iconPath && (
                                <MenuIcon active={isActive} item={item} />
                            )}
                            <div
                                className={cn(
                                    'items-center transition-all duration-750 ease-in-out whitespace-nowrap grow flex',
                                    {
                                        hidden: !isSidebarOpen,
                                    },
                                )}
                            >
                                <div className="text-inherit grow text-sm font-medium leading-tight">
                                    {item.alli360Name || item.name}
                                </div>
                                <div className="w-4 h-4">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 24 24"
                                        fill="currentColor"
                                        className="w-full h-full transition-transform transform ease-out"
                                        style={{
                                            transform: `${
                                                !isSubnavOpen
                                                    ? 'rotate(0deg)'
                                                    : 'rotate(90deg)'
                                            }`,
                                        }}
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M16.28 11.47a.75.75 0 0 1 0 1.06l-7.5 7.5a.75.75 0 0 1-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 0 1 1.06-1.06l7.5 7.5Z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                </div>
                            </div>
                        </div>
                    </>
                ) : (
                    <CreateLink
                        item={item}
                        navigateToUrl={navigateToUrl}
                        className={cn(
                            textStyles,
                            'flex items-center px-3 py-2 gap-2.5 h-auto w-full',
                        )}
                    >
                        {item?.iconPath && (
                            <MenuIcon active={isActive} item={item} />
                        )}
                        <div
                            className={cn(
                                'w-full transition-all opacity-0 duration-300 ease-in-out whitespace-nowrap hidden',
                                isSidebarOpen && 'opacity-100 flex',
                            )}
                        >
                            <span className="flex-grow text-sm font-medium leading-tight">
                                {item?.name}
                            </span>
                        </div>
                    </CreateLink>
                )}
            </div>
            <div
                className={cn(
                    'overflow-hidden max-h-0  opacity-0 transition-all ease-in-out duration-300',
                    isSubnavOpen && 'max-h-screen h-auto my-1 opacity-100',
                )}
            >
                {item.subNav?.map(subItem => {
                    return (
                        <CreateLink
                            key={subItem.id}
                            item={subItem}
                            navigateToUrl={navigateToUrl}
                            className={cn(
                                textStyles,
                                'mb-1 last:mb-0 w-full flex cursor-pointer pl-12 pr-3 rounded-md gap-2.5 py-2',
                                {
                                    [hoverStyles]: isSidebarOpen,
                                    [activeStyles]:
                                        activeSubnavItem === subItem.id,
                                },
                            )}
                        >
                            <span className="text-sm font-sm leading-tight whitespace-nowrap">
                                {subItem.name}
                            </span>
                        </CreateLink>
                    );
                })}
            </div>
        </>
    );
}
